import type { ActiveTimer, AgentRun, Label, Project, Session, Settings, SyncTable, Task } from '@timato/shared';
import type { EntityTable } from 'dexie';
import { db } from '@/db/db';
import { hlc } from '@/lib/device';
import { routeTeamWrite } from '@/team/writes';
import { useAuth } from '@/auth/store';

export type RowOf = { settings: Settings; activeTimer: ActiveTimer; labels: Label; projects: Project; tasks: Task; sessions: Session; agentRuns: AgentRun };
type AnyRow = RowOf[SyncTable];

const tables: { [T in SyncTable]: EntityTable<RowOf[T], 'id'> } = {
  settings: db.settings, activeTimer: db.activeTimer, labels: db.labels, projects: db.projects, tasks: db.tasks, sessions: db.sessions, agentRuns: db.agentRuns,
};

/**
 * The one write path for synced rows: stamps `updatedAt` with the device HLC, stores the row, and queues it in the
 * outbox for the sync client (Phase 2). Everything the UI changes goes through here so sync is never an afterthought.
 */
export async function saveRow<T extends SyncTable>(table: T, row: RowOf[T]): Promise<RowOf[T]> {
  const stamped = { ...row, updatedAt: hlc.now() } as RowOf[T];
  if ((table === 'projects' || table === 'tasks' || table === 'labels') && (await routeTeamWrite(table, stamped as Project | Task | Label))) return stamped;
  // Signed out there is nothing to drain the outbox, so skip it: signing in uploads the whole local database anyway.
  const queue = useAuth.getState().status === 'signedIn';
  await db.transaction('rw', [tables[table] as EntityTable<AnyRow, 'id'>, db.outbox], async () => {
    await (tables[table] as EntityTable<AnyRow, 'id'>).put(stamped);
    if (queue) await db.outbox.add({ table, rowId: stamped.id, op: 'upsert', payload: stamped as Record<string, unknown>, updatedAt: stamped.updatedAt });
  });
  return stamped;
}

/** Soft delete: tombstone the row so the deletion propagates through sync. */
export function deleteRow<T extends Exclude<SyncTable, 'settings' | 'activeTimer'>>(table: T, row: RowOf[T]) {
  return saveRow(table, { ...row, deletedAt: new Date().toISOString() });
}

export const notDeleted = <R extends { deletedAt: string | null }>(r: R) => r.deletedAt == null;
